/**
 * BUNKER 84 — software raycaster framebuffer renderer.
 *
 * Owns a single reusable `ImageData(480, 270)`. Each frame:
 *   1. Fill ceiling + floor as flat color bands using the Uint32Array
 *      view (one write per pixel — no fillRect overhead).
 *   2. For each screen column, cast a ray and fill the wall slice
 *      from `wallTop` to `wallBottom` with the wall's color, NS/EW
 *      shaded against the side the ray hit.
 *   3. Blit the whole buffer to the canvas via `putImageData`.
 *
 * All allocations happen once at construction. The hot loop touches
 * only pre-allocated typed arrays and a handful of locals — no
 * object literals, no array literals, no `new` anywhere.
 *
 * Color layout: little-endian RGBA → `0xAABBGGRR`. Yes, that looks
 * backwards. It's how `Uint32Array` writes into the `ImageData.data`
 * `Uint8ClampedArray` on every JS engine we care about. We pre-encode
 * each palette color once at module init.
 */

import { ENEMY_HEIGHT_TILES, type Enemy } from './enemies.js';
import {
  CEILING,
  FLOOR,
  HUD_HP_LOW,
  WALL_BRICK_DARK,
  WALL_BRICK_LIT,
  WALL_CONCRETE_DARK,
  WALL_CONCRETE_LIT,
  WALL_DOOR_DARK,
  WALL_DOOR_LIT,
  WALL_DOOR_LOCKED_DARK,
  WALL_DOOR_LOCKED_LIT,
  WALL_INSIGNIA_DARK,
  WALL_INSIGNIA_LIT,
  WALL_SECRET_DARK,
  WALL_SECRET_LIT,
  WALL_STEEL_DARK,
  WALL_STEEL_LIT,
  WALL_TILE_DARK,
  WALL_TILE_LIT,
} from './palette.js';
import type { Player } from './player.js';
import { castColumn } from './raycaster.js';

export const RENDER_W = 480;
export const RENDER_H = 270;

/** Field of view in radians. 60° matches the Wolf3D feel. */
const FOV = Math.PI / 3;
/** Camera plane half-length. tan(fov/2). */
const PLANE_LEN = Math.tan(FOV / 2);

/** Parse '#rrggbb' into the little-endian 0xAABBGGRR packed Uint32. */
function rgbaFromHex(hex: string, alpha = 0xff): number {
  // #rrggbb → 6 hex chars after '#'
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Little-endian: byte 0 = R, byte 1 = G, byte 2 = B, byte 3 = A.
  return (alpha << 24) | (b << 16) | (g << 8) | r;
}

/**
 * Wall color lookup keyed by tile id. Indexed [tileId][side: 0=EW, 1=NS]
 * → packed Uint32. Tile id 0 (empty) and any out-of-range tile fall
 * back to concrete.
 */
const WALL_COLORS_LIT: readonly number[] = [
  rgbaFromHex(WALL_CONCRETE_LIT), //  0 (unused — empty tile)
  rgbaFromHex(WALL_CONCRETE_LIT), //  1 concrete
  rgbaFromHex(WALL_STEEL_LIT),    //  2 steel
  rgbaFromHex(WALL_BRICK_LIT),    //  3 brick
  rgbaFromHex(WALL_TILE_LIT),     //  4 utility tile
  rgbaFromHex(WALL_DOOR_LIT),     //  5 door
  rgbaFromHex(WALL_DOOR_LOCKED_LIT), // 6 locked door
  rgbaFromHex(WALL_SECRET_LIT),   //  7 secret (visually concrete)
  rgbaFromHex(WALL_INSIGNIA_LIT), //  8 insignia
];
const WALL_COLORS_DARK: readonly number[] = [
  rgbaFromHex(WALL_CONCRETE_DARK),
  rgbaFromHex(WALL_CONCRETE_DARK),
  rgbaFromHex(WALL_STEEL_DARK),
  rgbaFromHex(WALL_BRICK_DARK),
  rgbaFromHex(WALL_TILE_DARK),
  rgbaFromHex(WALL_DOOR_DARK),
  rgbaFromHex(WALL_DOOR_LOCKED_DARK),
  rgbaFromHex(WALL_SECRET_DARK),
  rgbaFromHex(WALL_INSIGNIA_DARK),
];

const CEILING_RGBA = rgbaFromHex(CEILING);
const FLOOR_RGBA = rgbaFromHex(FLOOR);
const ENEMY_BODY_RGBA = rgbaFromHex(HUD_HP_LOW); // v0.1 placeholder — red rectangle
const ENEMY_DEAD_RGBA = rgbaFromHex('#3a1014');  // dim corpse tone

/** Per-frame metrics for the F-key perf overlay. */
export interface FrameTiming {
  /** Milliseconds spent in the raycast + column-fill loop. */
  raycastMs: number;
  /** Milliseconds spent in `putImageData`. */
  blitMs: number;
}

export class Renderer {
  private readonly buffer: ImageData;
  /** Uint32 view over `buffer.data` — one write per pixel. */
  private readonly pixels: Uint32Array;
  /**
   * Per-column wall distance written during the wall pass and read
   * during the sprite pass for occlusion. One entry per screen column.
   */
  private readonly depth: Float32Array;
  /** Cached so the F-key overlay can read last frame's timings. */
  readonly lastTiming: FrameTiming = { raycastMs: 0, blitMs: 0 };

  constructor() {
    this.buffer = new ImageData(RENDER_W, RENDER_H);
    // `Uint32Array(buffer)` creates a typed-array view sharing memory
    // with the underlying ArrayBuffer — writes here land directly in
    // ImageData.data, no copy.
    this.pixels = new Uint32Array(this.buffer.data.buffer);
    this.depth = new Float32Array(RENDER_W);
  }

  /**
   * Render one frame to the supplied canvas context. Mutates the
   * internal buffer; safe to call from `GameInstance.render` because
   * we don't touch any game state.
   */
  render(
    g: CanvasRenderingContext2D,
    player: Player,
    map: Uint8Array,
    enemies: readonly Enemy[]
  ): void {
    const t0 = performance.now();

    this.fillCeilingFloor();
    this.castWalls(player, map);
    this.drawEnemies(player, enemies);

    const t1 = performance.now();
    g.putImageData(this.buffer, 0, 0);
    const t2 = performance.now();

    this.lastTiming.raycastMs = t1 - t0;
    this.lastTiming.blitMs = t2 - t1;
  }

  private fillCeilingFloor(): void {
    const pixels = this.pixels;
    const halfH = RENDER_H >> 1;
    const ceiling = CEILING_RGBA;
    const floor = FLOOR_RGBA;
    // Top half = ceiling.
    const topEnd = halfH * RENDER_W;
    for (let i = 0; i < topEnd; i++) {
      pixels[i] = ceiling;
    }
    // Bottom half = floor.
    const total = RENDER_W * RENDER_H;
    for (let i = topEnd; i < total; i++) {
      pixels[i] = floor;
    }
  }

  private castWalls(player: Player, map: Uint8Array): void {
    const pixels = this.pixels;
    const halfH = RENDER_H >> 1;

    // Pre-compute facing + plane vectors once per frame.
    const dirX = Math.cos(player.angle);
    const dirY = Math.sin(player.angle);
    const planeX = -dirY * PLANE_LEN; // perpendicular, rotated +90°
    const planeY = dirX * PLANE_LEN;

    for (let col = 0; col < RENDER_W; col++) {
      const hit = castColumn(
        player.x,
        player.y,
        dirX,
        dirY,
        planeX,
        planeY,
        map,
        col,
        RENDER_W
      );
      const dist = hit.distance;

      // Wall slice height in pixels = screenHeight / perpDist.
      // Pre-multiply by halfH so we can derive top + bottom in one shot.
      const lineHalf = Math.floor(halfH / dist);
      let top = halfH - lineHalf;
      let bot = halfH + lineHalf;
      if (top < 0) top = 0;
      if (bot > RENDER_H) bot = RENDER_H;

      // NS/EW shading: 'y' side (horizontal grid line) reads darker
      // to imply lights mounted on EW walls.
      const tableIdx = hit.wallType < WALL_COLORS_LIT.length ? hit.wallType : 1;
      const color = hit.side === 'y'
        ? WALL_COLORS_DARK[tableIdx]!
        : WALL_COLORS_LIT[tableIdx]!;

      // Fill the column slice. Stride = RENDER_W between rows in the
      // 1-D pixel array.
      let p = top * RENDER_W + col;
      for (let y = top; y < bot; y++) {
        pixels[p] = color;
        p += RENDER_W;
      }

      this.depth[col] = dist;
    }
  }

  /**
   * Render enemy billboards. v0.1 = solid colored rectangles per enemy,
   * with proper depth occlusion against the wall buffer written above.
   * Real sprite billboards arrive in sc-4542.
   *
   * For each enemy: transform to camera space using the inverse of the
   * (dir, plane) basis, derive screen-space height + horizontal extent,
   * then for each column the enemy covers, write only where its
   * distance is less than the column's wall distance.
   */
  private drawEnemies(player: Player, enemies: readonly Enemy[]): void {
    const pixels = this.pixels;
    const depth = this.depth;
    const halfH = RENDER_H >> 1;

    const dirX = Math.cos(player.angle);
    const dirY = Math.sin(player.angle);
    const planeX = -dirY * PLANE_LEN;
    const planeY = dirX * PLANE_LEN;

    // Inverse of the 2x2 matrix [planeX dirX; planeY dirY].
    const invDet = 1 / (planeX * dirY - dirX * planeY);

    // Collect-and-sort would be cleaner but allocates; v0.1 has ≤5
    // enemies so a simple max-distance-first selection pass is plenty.
    // Iterate enemies in source order — distance is small enough that
    // back-to-front draw isn't critical at the rectangle stage.
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i]!;
      if (e.state === 'dead') continue;

      const sx = e.x - player.x;
      const sy = e.y - player.y;

      // Transform sprite to camera space.
      const transformX = invDet * (dirY * sx - dirX * sy);
      const transformY = invDet * (-planeY * sx + planeX * sy);

      // Behind the camera — skip.
      if (transformY <= 0) continue;

      const screenX = Math.floor((RENDER_W / 2) * (1 + transformX / transformY));

      // Sprite is ENEMY_HEIGHT_TILES tall; scale by 1/transformY for perspective.
      const spriteHeight = Math.floor((RENDER_H * ENEMY_HEIGHT_TILES) / transformY);
      const spriteWidth = Math.floor(spriteHeight * 0.5); // narrower than tall
      // Anchor the sprite's feet to the floor line by offsetting downward.
      const drawTop = halfH - (spriteHeight >> 1) + Math.floor(spriteHeight * 0.15);
      const drawBot = Math.min(RENDER_H, drawTop + spriteHeight);
      const top = Math.max(0, drawTop);

      const half = spriteWidth >> 1;
      const colStart = Math.max(0, screenX - half);
      const colEnd = Math.min(RENDER_W, screenX + half);
      const color = e.state === 'dying' ? ENEMY_DEAD_RGBA : ENEMY_BODY_RGBA;

      for (let col = colStart; col < colEnd; col++) {
        // Wall occludes if it's closer than the sprite.
        if (depth[col]! <= transformY) continue;
        let p = top * RENDER_W + col;
        for (let y = top; y < drawBot; y++) {
          pixels[p] = color;
          p += RENDER_W;
        }
      }
    }
  }
}
